import {
  Alert,
  AlertIcon,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
} from "@chakra-ui/react";
import EntityModalButton from "../entityProfileModal/components/EntityModalButton";
import { caregiverProfileSections } from "../caregiverProfileModal/caregiverProfileModal.sections";
import { patientProfileSections } from "../patientProfileModal/patientProfileModal.sections";
import { DEFAULT_PROFILE_WINDOW_SECTION, LAST_OPENED_SECTION } from "./profileWindowSettings.store";
import useProfileWindowSettings from "./useProfileWindowSettings";

type Props = {
  onClose: () => void;
};

const caregiverSectionLabels = caregiverProfileSections.map((section) => section.label);
const patientSectionLabels = patientProfileSections.map((section) => section.label);

function SectionSelect(props: {
  isDisabled: boolean;
  label: string;
  sectionLabels: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <FormControl isDisabled={props.isDisabled}>
      <FormLabel fontSize="14px" fontWeight={600} mb={1}>
        {props.label}
      </FormLabel>
      <Select
        isDisabled={props.isDisabled}
        size="sm"
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      >
        <option value={LAST_OPENED_SECTION}>Last opened section</option>
        {props.sectionLabels.map((sectionLabel) => (
          // Only the visible text is annotated — the stored value stays the bare
          // section label so it keeps matching the navigation's section labels.
          <option key={sectionLabel} value={sectionLabel}>
            {sectionLabel === DEFAULT_PROFILE_WINDOW_SECTION
              ? `${sectionLabel} (default)`
              : sectionLabel}
          </option>
        ))}
      </Select>
    </FormControl>
  );
}

export default function ProfileWindowSettingsModal(props: Props) {
  const [settings, updateSettings] = useProfileWindowSettings();
  // Every other option only describes the beta window, so they are inert while
  // the legacy window is selected.
  const areOptionsDisabled = !settings.useBetaWindow;

  return (
    <Modal isOpen size="lg" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="18px">Profile window settings</ModalHeader>
        <Divider />
        <ModalCloseButton onClick={props.onClose} />
        <ModalBody>
          <Flex direction="column" gap={5} py={2}>
            {areOptionsDisabled ? (
              <Alert borderRadius="4px" fontSize="13px" status="info">
                <AlertIcon />
                These settings apply to the new (beta) window only. Turn it on under Beta features,
                in the settings menu at the bottom of the sidebar.
              </Alert>
            ) : null}

            <FormControl isDisabled={areOptionsDisabled}>
              <Checkbox
                isChecked={settings.saveSubsectionFold}
                isDisabled={areOptionsDisabled}
                onChange={(event) => updateSettings({ saveSubsectionFold: event.target.checked })}
              >
                Save subsection fold
              </Checkbox>
              <FormHelperText mt={1}>
                Remember which subsections you collapsed and reopen them that way next time.
              </FormHelperText>
            </FormControl>

            <FormControl isDisabled={areOptionsDisabled}>
              <Checkbox
                isChecked={settings.saveCalendarSelection}
                isDisabled={areOptionsDisabled}
                onChange={(event) =>
                  updateSettings({ saveCalendarSelection: event.target.checked })
                }
              >
                Save calendar checkmarks
              </Checkbox>
              <FormHelperText mt={1}>
                Keep selected days when changing months or tabs in this profile. Closing the profile
                clears the selection.
              </FormHelperText>
            </FormControl>

            <FormControl isDisabled={areOptionsDisabled}>
              <Checkbox
                isChecked={settings.showCalendarHoverIndication}
                isDisabled={areOptionsDisabled}
                onChange={(event) =>
                  updateSettings({ showCalendarHoverIndication: event.target.checked })
                }
              >
                Calendar blue indication
              </Checkbox>
              <FormHelperText mt={1}>
                Highlight the calendar row and column under the cursor in blue.
              </FormHelperText>
            </FormControl>

            <Divider />

            <Flex direction="column" gap={1}>
              <Text fontSize="14px" fontWeight={700}>
                Open window on section
              </Text>
              <Text color="gray.600" fontSize="13px">
                Pick the section each window starts on, or let it resume wherever you were last.
              </Text>
            </Flex>

            <Flex direction={{ base: "column", md: "row" }} gap={4}>
              <SectionSelect
                isDisabled={areOptionsDisabled}
                label="Patient"
                sectionLabels={patientSectionLabels}
                value={settings.openPatientWindowOnSection}
                onChange={(value) => updateSettings({ openPatientWindowOnSection: value })}
              />
              <SectionSelect
                isDisabled={areOptionsDisabled}
                label="Caregiver"
                sectionLabels={caregiverSectionLabels}
                value={settings.openCaregiverWindowOnSection}
                onChange={(value) => updateSettings({ openCaregiverWindowOnSection: value })}
              />
            </Flex>
          </Flex>
        </ModalBody>
        <Divider />
        <ModalFooter>
          <EntityModalButton kind="blue" size="sm" onClick={props.onClose}>
            Done
          </EntityModalButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
